import type { ITurnHandoffEvent, TurnHandoffDisposition } from "./chat.types";

export const ASSISTANT_MESSAGE_ID_HEADER = "X-Assistant-Message-Id";

const ASSISTANT_MESSAGE_ID_PATTERN = /^[1-9]\d*$/;
const TURN_HANDOFF_KEYS = [
    "type",
    "assistant_message_id",
    "disposition",
] as const;
// `satisfies` is what makes this exhaustive: a disposition added to the union
// without a key here fails the build rather than being rejected at runtime as
// unknown.
const TURN_HANDOFF_DISPOSITIONS = {
    completed_at_rest: true,
    queued: true,
} as const satisfies Record<TurnHandoffDisposition, true>;

export class ChatStreamProtocolError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ChatStreamProtocolError";
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPositiveSafeInteger(value: unknown): value is number {
    return (
        typeof value === "number" && Number.isSafeInteger(value) && value > 0
    );
}

function isTurnHandoffDisposition(
    value: unknown
): value is TurnHandoffDisposition {
    return (
        typeof value === "string" &&
        Object.prototype.hasOwnProperty.call(TURN_HANDOFF_DISPOSITIONS, value)
    );
}

function hasExactHandoffKeys(value: Record<string, unknown>): boolean {
    const keys = Object.keys(value);
    return (
        keys.length === TURN_HANDOFF_KEYS.length &&
        TURN_HANDOFF_KEYS.every((key) =>
            Object.prototype.hasOwnProperty.call(value, key)
        )
    );
}

export function parseAssistantMessageIdHeader(value: unknown): number {
    if (
        typeof value !== "string" ||
        !ASSISTANT_MESSAGE_ID_PATTERN.test(value)
    ) {
        throw new ChatStreamProtocolError(
            `${ASSISTANT_MESSAGE_ID_HEADER} must be a positive safe integer`
        );
    }
    const assistantMessageId = Number(value);
    if (!isPositiveSafeInteger(assistantMessageId)) {
        throw new ChatStreamProtocolError(
            `${ASSISTANT_MESSAGE_ID_HEADER} must be a positive safe integer`
        );
    }
    return assistantMessageId;
}

export class StrictTurnHandoffParser {
    private handoff: ITurnHandoffEvent | null = null;

    constructor(private readonly assistantMessageId: number) {
        if (!isPositiveSafeInteger(assistantMessageId)) {
            throw new ChatStreamProtocolError(
                "The expected assistant message id must be a positive safe integer"
            );
        }
    }

    parse(event: unknown): ITurnHandoffEvent | null {
        if (!isRecord(event) || event.type !== "turn_handoff") return null;
        if (this.handoff !== null) {
            throw new ChatStreamProtocolError(
                "The stream emitted more than one turn handoff"
            );
        }
        if (!hasExactHandoffKeys(event)) {
            throw new ChatStreamProtocolError(
                "The turn handoff envelope has an invalid shape"
            );
        }
        if (!isPositiveSafeInteger(event.assistant_message_id)) {
            throw new ChatStreamProtocolError(
                "The turn handoff assistant message id must be a positive safe integer"
            );
        }
        if (!isTurnHandoffDisposition(event.disposition)) {
            throw new ChatStreamProtocolError(
                "The turn handoff disposition is unknown"
            );
        }
        if (event.assistant_message_id !== this.assistantMessageId) {
            throw new ChatStreamProtocolError(
                "The turn handoff assistant message id does not match the response header"
            );
        }
        this.handoff = {
            type: "turn_handoff",
            assistant_message_id: event.assistant_message_id,
            disposition: event.disposition,
        };
        return this.handoff;
    }
}
